import Button from 'tio-ui/components/button';
import Input from 'tio-ui/components/input';
import ReSelect from 'tio-ui/components/select';
import Footer from 'tio-ui/components/footer';
import Modal from 'tio-ui/components/modal';
import { not } from 'tio-ui/utilities';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import { dropTask } from 'ember-concurrency';
import type { DependentExtension as DependentModel } from 'tio-data/data/resources/dependent.ext';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type EmployeeService from 'tio-employee/services/employee';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import { saveRecord } from 'tio-data/requests/save-record';
import { PhUserPlus } from 'ember-phosphor-icons';

export interface AddDependentSignature {
  Args: {
    employee: EmployeeModel;
    // Primarily for test runner
    renderInPlace?: boolean;
  };
  Element: HTMLDivElement;
}

const relationships = [
  { key: 'DependentRelationship.CHILD', value: 'Child' },
  { key: 'DependentRelationship.SPOUSE', value: 'Spouse' },
  { key: 'DependentRelationship.PARTNER', value: 'Partner' },
];

export default class AddDependent extends Component<AddDependentSignature> {
  @service declare store: StoreService;
  @service declare employee: EmployeeService;

  @tracked show = false;
  @tracked firstName?: string;
  @tracked lastName?: string;
  @tracked birthDate?: string;
  @tracked relationship = '';

  get submitEnabled() {
    return this.firstName && this.lastName && this.birthDate && this.relationship;
  }

  get birthDateErrors() {
    const now = new Date().toISOString();
    if (this.birthDate! > now) {
      return ['Birth Date cannot be in the future.'];
    }
    return [];
  }

  clearFormData() {
    this.firstName = '';
    this.lastName = '';
    this.birthDate = '';
    this.relationship = '';
  }

  setRelationship = (value: string | null) => {
    this.relationship = value ?? '';
  };

  openDependentDialog = () => {
    this.show = !this.show;
  };

  submit = dropTask(async () => {
    const employee = this.args.employee;
    const dependents = this.args.employee.dependents;
    const newDependent = this.store.createRecord<DependentModel>('dependent', {
      firstName: this.firstName!,
      lastName: this.lastName!,
      birthDate: this.birthDate!,
      relationship: this.relationship,
      employee: employee,
    });
    dependents.push(newDependent);
    await saveRecord(this.store, newDependent);
    this.clearFormData();
    this.show = !this.show;
  });

  cancel = () => {
    this.show = !this.show;
  };

  <template>
    <div class="flex w-full" ...attributes>
      <Button
        {{on "click" this.openDependentDialog}}
        @appearance="outlined"
        @intent="primary"
        data-test-open-add-dependent-dialog
      >
        <span class="tio-row-flush">
          <PhUserPlus @size="20" class="mr-2" />
          <p class="mt-auto">{{t "common.tuition_assistance.add_dependent_button"}}</p>
        </span>
      </Button>
    </div>
    <Modal @isOpen={{this.show}} @onClose={{this.cancel}}>
      <:header>
        <h2 class="text-base" data-test-add-dependent-dialog>
          {{t "common.tuition_assistance.add_dependent_modal.header"}}
        </h2>
      </:header>
      <:body>
        <form class="tio-bg-surface md:col-span-2 pb-8">
          <div class="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <Input
                @label={{t "common.tuition_assistance.add_dependent_modal.first_name"}}
                @value={{this.firstName}}
                @onChange={{fn (mut this.firstName)}}
                data-test-add-dependent-first-name
              />
            </div>

            <div class="sm:col-span-3">
              <Input
                @label={{t "common.tuition_assistance.add_dependent_modal.last_name"}}
                @value={{this.lastName}}
                @onChange={{fn (mut this.lastName)}}
                data-test-add-dependent-last-name
              />
            </div>

            <div class="sm:col-span-6 mr-6">
              <div class="w-1/2 mt-2">
                <ReSelect
                  @label={{t "common.tuition_assistance.add_dependent_modal.relationship"}}
                  @items={{relationships}}
                  @selected={{this.relationship}}
                  @onChange={{this.setRelationship}}
                  data-test-id="trigger"
                />
              </div>
            </div>

            <div class="sm:col-span-3">
              <div class="mt-2">
                <Input
                  @label={{t "common.tuition_assistance.add_dependent_modal.birth_date"}}
                  @type="date"
                  @value={{this.birthDate}}
                  @onChange={{fn (mut this.birthDate)}}
                  @errors={{this.birthDateErrors}}
                  data-test-add-dependent-birth-date
                />
              </div>
            </div>
          </div>
        </form>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.submit.isRunning}}
          @onSubmit={{this.submit.perform}}
          @onCancel={{this.cancel}}
          @submitText="Add"
          @submitDisabled={{not this.submitEnabled}}
        />
      </:footer>
    </Modal>
  </template>
}
